import { isSome } from "@sveabank/cc-common";
import { useApplicationId } from "../../hooks/useApplicationId";
import { useSteps } from "../../hooks/useSteps";
import {
	useAssignFullAccessUsersMutation,
	useConfigureMastercardMutation,
	useFetchApplicationDetailsQuery,
	useFetchFullAccessUsersQuery,
	useFinalizeApplicationMutation,
	useRemoveFullAccessUsersMutation,
} from "../../services/application";
import { useFetchSelectedPackageQuery } from "../../services/package";
import type {
	ConfigureCardState,
	ConfigureMastercardCreditStep,
	ConfigureMastercardDirectStep,
} from "../../types/step";
import { useContactForm } from "../contact-info-page/hooks";

export const useApplicationSettings = () => {
	const applicationId = useApplicationId();
	const { setHasFormError, countries } = useContactForm();
	const { steps = [], isLoading: isLoadingSteps } = useSteps();
	const { data: applicationDetails, isLoading: isApplicationDetailsLoading } =
		useFetchApplicationDetailsQuery(applicationId);

	const { data: selectedPackage, isLoading: isSelectedPackageLoading } =
		useFetchSelectedPackageQuery(applicationId);

	const [finalizeApplicationMutation] = useFinalizeApplicationMutation();

	const finalizeApplication = async () => {
		await finalizeApplicationMutation(applicationId).unwrap();
	};

	const [configureMastercard] = useConfigureMastercardMutation();

	const { data: fullAccessUsers, isLoading: isLoadingFullAccessUsers } =
		useFetchFullAccessUsersQuery(applicationId);
	const [assignFullAccessUsersMutation] = useAssignFullAccessUsersMutation();
	const [removeFullAccessUsersMutation] = useRemoveFullAccessUsersMutation();

	const assignFullAccessUsers = async (users: { nationalIds: string[] }) => {
		await assignFullAccessUsersMutation({
			applicationId,
			fullAccessUsers: users,
		}).unwrap();
	};

	const removeFullAccessUsers = async () => {
		await removeFullAccessUsersMutation(applicationId).unwrap();
	};

	const isLoading = isSome(
		isLoadingSteps,
		isApplicationDetailsLoading,
		isLoadingFullAccessUsers,
		isSelectedPackageLoading,
	);

	const cardsToConfigure = steps
		.filter(
			(step): step is ConfigureMastercardCreditStep | ConfigureMastercardDirectStep =>
				step.name === "ConfigureMastercardCreditStep" ||
				step.name === "ConfigureMastercardDirectStep",
		)
		.map((step) => ({
			...step.state,
			name: step.name,
			status: step.status,
		})) as ConfigureCardState[];

	return {
		applicationDetails,
		fullAccessUsers: fullAccessUsers || [],
		assignFullAccessUsers,
		removeFullAccessUsers,
		cardsToConfigure,
		isLoading,
		finalizeApplication,
		setHasFormError,
		applicationId,
		countries,
		configureMastercard,
		steps,
		selectedPackage,
	};
};
